import React from 'react';

/**
 * Utility functions for player-related operations
 */

/**
 * Get gender-appropriate color class for player names.
 * Uses semantic tokens so male names are visible in dark mode (--color-male is light in .dark).
 * @param {string} gender - Player gender ('male', 'female', etc.)
 * @returns {string} - Tailwind CSS color class
 */
export const getPlayerNameColor = (gender) => {
  if (!gender || typeof gender !== 'string') return 'text-male';
  return gender.toLowerCase() === 'female' ? 'text-female font-semibold' : 'text-male';
};

/**
 * Format player name with gender-appropriate styling
 * @param {Object} player - Player object with first_name, last_name, gender, and optional archived guest fields
 * @param {string} variant - Typography variant (default: 'body')
 * @param {string} className - Additional CSS classes
 * @param {boolean} showArchivedBadge - Whether to show archived guest badge (default: true)
 * @returns {React.Element} - Formatted player name element
 */
export const formatPlayerName = (player, variant = 'body', className = '', showArchivedBadge = true) => {
  if (!player) return <span className="text-text-muted">Unknown</span>;
  
  const fullName = getPlayerFullName(player);
  const colorClass = getPlayerNameColor(player.gender);
  
  // Check if this is an archived guest
  // A guest is archived only if they are inactive (is_active = false)
  // This matches the logic in the player_game_history view
  const isArchivedGuest = showArchivedBadge && (
    player.player_is_archived_guest === true ||
    (player.is_guest === true && player.is_active === false)
  );
  
  // Base typography classes based on variant
  const variantClasses = {
    'body': 'text-base leading-relaxed',
    'body-small': 'text-sm leading-relaxed',
    'body-large': 'text-lg leading-relaxed',
    'caption': 'text-xs leading-normal',
    'label': 'text-sm font-medium leading-normal'
  };
  
  const baseClasses = variantClasses[variant] || variantClasses['body'];
  
  return (
    <span className={`${baseClasses} ${colorClass} ${className} inline-flex items-center gap-1.5`}>
      <span>{fullName}</span>
      {isArchivedGuest && (
        <span 
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warning-light text-warning-dark border border-warning"
          title="Archived guest account - this player's account has been merged or declined"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <span>Archived</span>
        </span>
      )}
    </span>
  );
};

/**
 * Get player's full name as a string
 * @param {Object} player - Player object with first_name, last_name
 * @returns {string} - Full name string
 */
export const getPlayerFullName = (player) => {
  if (!player) return 'Unknown';
  const fromSnake = `${player.first_name || ''} ${player.last_name || ''}`.trim();
  if (fromSnake) return fromSnake;
  const fromCamel = `${player.firstName || ''} ${player.lastName || ''}`.trim();
  if (fromCamel) return fromCamel;
  return (player.name || '').trim() || 'Unknown';
};

/**
 * Whether a player matches a free-text search (name parts, full name, email, optional payment aliases).
 */
export const playerMatchesSearchQuery = (player, query, { includePaymentAliases = false } = {}) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if (!player) return false;

  const fullName = getPlayerFullName(player).toLowerCase();
  if (fullName !== 'unknown' && fullName.includes(q)) return true;

  const first = (player.first_name || player.firstName || '').toLowerCase();
  const last = (player.last_name || player.lastName || '').toLowerCase();
  if (first.includes(q) || last.includes(q)) return true;

  const email = (player.email || '').toLowerCase();
  if (email.includes(q)) return true;

  if (includePaymentAliases && Array.isArray(player.payment_search_aliases)) {
    return player.payment_search_aliases.some(
      (alias) => alias && String(alias).toLowerCase().includes(q)
    );
  }

  return false;
};

/**
 * Session games count for compact court UI: plain number, or "n, Late" when effective count differs.
 * @param {Object} player
 * @returns {string}
 */
export const getEffectiveGamesThisSession = (player) => {
  if (player?.effectiveGamesPlayed !== undefined && player.effectiveGamesPlayed !== null) {
    return Number(player.effectiveGamesPlayed) || 0;
  }
  return Number(player?.gamesPlayedThisSession ?? 0) || 0;
};

/**
 * Why a host cannot hard-remove a player from a live session (null = allowed).
 * @param {Object} options
 * @param {string} [options.playerId]
 * @param {Object} [options.player]
 * @param {boolean} [options.isOnCourt]
 * @param {boolean} [options.isInUpcoming]
 * @param {Object} [options.sessionPlayerScheduledGames]
 * @param {Object} [options.partnershipLookup]
 * @param {Object} [options.playersById]
 * @returns {string|null}
 */
export const getRemoveFromSessionBlockReason = ({
  playerId,
  player,
  isOnCourt = false,
  isInUpcoming = false,
  sessionPlayerScheduledGames = {},
  partnershipLookup = {},
  playersById = {},
}) => {
  const id = playerId || player?.id;
  if (!id) return 'Player not found';

  if (isOnCourt) {
    return 'Remove them from the court or finish the match first.';
  }
  if (isInUpcoming) {
    return 'Remove them from the upcoming match first.';
  }
  if (getEffectiveGamesThisSession(player) > 0) {
    return 'This player has already played — use Finished instead.';
  }
  if (sessionPlayerScheduledGames[id]) {
    return 'Cancel their scheduled game before removing them from the session.';
  }
  const partnerId = partnershipLookup[id];
  if (partnerId) {
    const partner = playersById[partnerId];
    const partnerName =
      partner?.name ||
      [partner?.first_name, partner?.last_name].filter(Boolean).join(' ') ||
      'their partner';
    return `Remove or change the partnership with ${partnerName} before removing this player.`;
  }
  return null;
};

export const canRemovePlayerFromSession = (options) =>
  getRemoveFromSessionBlockReason(options) === null;

export const formatSessionGamesCountLabel = (player) => {
  if (!player) return '0';
  const games = player.gamesPlayedThisSession ?? 0;
  const effective = player.effectiveGamesPlayed;
  const isLate = effective !== undefined && effective !== games;
  const n = Number(isLate ? effective : games);
  const count = Number.isFinite(n) ? n : 0;
  return isLate ? `${count}, Late` : String(count);
};

/**
 * Stable display order for players on the same team (alphabetical by name, then id).
 * Matchmaking/DB may return team arrays in arbitrary order; this keeps the UI consistent.
 */
export const sortTeamMemberIdsForDisplay = (playerIds, playersById = {}) => {
  if (!Array.isArray(playerIds) || playerIds.length <= 1) {
    return playerIds ? [...playerIds] : [];
  }
  return [...playerIds].sort((a, b) => {
    const pa = playersById[a];
    const pb = playersById[b];
    const na = (pa?.name || getPlayerFullName(pa) || '').trim();
    const nb = (pb?.name || getPlayerFullName(pb) || '').trim();
    const cmp = na.localeCompare(nb, undefined, { sensitivity: 'base', numeric: true });
    if (cmp !== 0) return cmp;
    return String(a).localeCompare(String(b));
  });
};

/**
 * Compact name for dense UI (e.g. mobile cards): "First L." when surname exists, else first name only.
 * Same gender colouring as formatPlayerName; optional tiny marker for archived guests.
 */
export const formatPlayerNameCompact = (
  player,
  { className = '', showArchivedBadge = true } = {}
) => {
  if (!player) {
    return <span className="text-text-muted">?</span>;
  }
  const first = (player.first_name || player.firstName || '').trim();
  const last = (player.last_name || player.lastName || '').trim();
  const colorClass = getPlayerNameColor(player.gender);
  let text = first || getPlayerFullName(player);
  if (text === 'Unknown') text = '?';
  if (last.length > 0) {
    text = `${first} ${last.charAt(0).toUpperCase()}.`;
  }
  const isArchivedGuest =
    showArchivedBadge &&
    (player.player_is_archived_guest === true ||
      (player.is_guest === true && player.is_active === false));

  return (
    <span className={`inline-flex max-w-full items-center gap-0.5 ${colorClass} ${className}`}>
      <span className="truncate font-medium">{text}</span>
      {isArchivedGuest && (
        <span
          className="shrink-0 rounded bg-warning-light px-0.5 text-[9px] font-bold leading-none text-warning-dark"
          title="Archived guest"
          aria-label="Archived guest"
        >
          ⌁
        </span>
      )}
    </span>
  );
};

/**
 * Get player's initials
 * @param {Object} player - Player object with first_name, last_name
 * @returns {string} - Player initials (e.g., "JD" for John Doe)
 */
export const getPlayerInitials = (player) => {
  if (!player) return '??';
  const first = player.first_name?.[0] || '';
  const last = player.last_name?.[0] || '';
  return `${first}${last}`.toUpperCase();
};

/**
 * `player.singles_elo_rating` from `player_club_elo_ratings` (current season). Null when missing/invalid — do not substitute doubles Elo.
 * @param {object|null|undefined} player
 * @returns {number|null}
 */
export const getSinglesClubEloRating = (player) => {
  if (!player) return null;
  const raw = player.singles_elo_rating;
  if (raw == null || raw === '') return null;
  const s = Number(raw);
  if (!Number.isFinite(s)) return null;
  return Math.round(s);
};

/** Matchmaking / simulation only: numeric fallback when club singles Elo is unset. */
export const getSinglesEloForComputation = (player, fallback = 1200) =>
  getSinglesClubEloRating(player) ?? fallback;

/** Render Elo in UI (nullable singles vs numeric doubles). */
export const formatEloForUi = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return String(value);
};

/**
 * Ratings tooltip line for competitive session match type preference (wait metrics / normalized).
 * Returns null when there is nothing meaningful to show.
 * @param {{ matchPreference?: string, preferenceStrength?: string }} [slice]
 * @returns {string|null}
 */
export function formatSessionMatchPreferenceTooltipLine(slice) {
  if (!slice || typeof slice !== 'object') return null;
  const pref = slice.matchPreference;
  if (!pref || pref === 'no_preference') return null;

  const strength = slice.preferenceStrength || 'soft';
  const strengthLabel = strength === 'hard' ? 'Hard' : 'Soft';

  if (pref === 'prefer_mixed') {
    return `Match preference: Prefers mixed doubles (${strengthLabel})`;
  }
  if (pref === 'prefer_levels') {
    return `Match preference: Prefers levels / same-gender doubles (${strengthLabel})`;
  }
  return null;
}

/**
 * Ratings tooltip line for session mens-doubles eligibility (host-assigned shadow Elo).
 * @param {{ mensDoublesEligible?: boolean, mensDoublesShadowElo?: number|null }} [slice]
 * @returns {string|null}
 */
export function formatSessionMensDoublesTooltipLine(slice) {
  if (!slice?.mensDoublesEligible) return null;
  const shadow = slice.mensDoublesShadowElo;
  if (shadow != null && Number.isFinite(Number(shadow))) {
    return `Mens Elo: ${formatEloForUi(Number(shadow))}`;
  }
  return 'Mens doubles eligible (Elo not set)';
}

/**
 * Multi-line tooltip: full name, session games, mixed & levels Elo, optional club singles, optional post-match Elo change.
 */
export function buildPlayerEloTooltipText(player, options = {}) {
  const {
    useSinglesElo = false,
    eloChangeTotal = null,
    sessionWaitMetrics = null,
  } = options;
  if (!player) return '';
  const name = (player.fullName || player.name || '').trim() || getPlayerFullName(player);
  const games = formatSessionGamesCountLabel(player);
  const lines = [
    name,
    `Games this session: ${games}`,
    `Mixed Elo: ${formatEloForUi(player.mixed_elo_rating)}`,
    `Levels Elo: ${formatEloForUi(player.levels_elo_rating)}`,
  ];
  if (useSinglesElo) {
    lines.push(`Club singles Elo: ${formatEloForUi(getSinglesClubEloRating(player))}`);
  }
  if (eloChangeTotal != null && eloChangeTotal !== 0) {
    lines.push(`Elo change: ${eloChangeTotal > 0 ? '+' : ''}${eloChangeTotal}`);
  }

  const prefLine = formatSessionMatchPreferenceTooltipLine(sessionWaitMetrics);
  if (prefLine) lines.push(prefLine);

  const mensLine = formatSessionMensDoublesTooltipLine(sessionWaitMetrics);
  if (mensLine) lines.push(mensLine);

  return lines.join('\n');
}

/**
 * Get appropriate ELO rating based on teammate gender
 * Returns mixed ELO for mixed-gender teams, levels ELO for same-gender teams
 * @param {Object} player - Player object with mixed_elo_rating, levels_elo_rating, gender
 * @param {Object} teammate - Teammate object with gender (optional)
 * @returns {number} - ELO rating to display
 */
/**
 * @param {object|null} player
 * @param {object|null} [teammate] - other player on same team (doubles)
 * @param {boolean} [useSinglesElo] - competitive 1v1: `singles_elo_rating` from DB, or null if unset
 */
export const getDisplayElo = (player, teammate = null, useSinglesElo = false) => {
  if (!player) return useSinglesElo ? null : 0;

  if (useSinglesElo) {
    return getSinglesClubEloRating(player);
  }

  // If we have both player and teammate with gender info
  if (teammate?.gender && player.gender) {
    // Mixed gender team -> use mixed ELO
    if (teammate.gender !== player.gender) {
      return player.mixed_elo_rating || 0;
    }
    // Same gender team -> use levels ELO
    return player.levels_elo_rating || 0;
  }

  // Default to levels ELO if no teammate info (doubles incomplete slot, etc.)
  return player.levels_elo_rating || 0;
};
